import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Building2, 
  Users, 
  ArrowRight,
  ExternalLink,
  Layers,
  Plus,
  Globe,
  ShoppingCart,
  Droplets,
  Truck,
  Wrench
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";
import CloverDiagnostics from "@/components/CloverDiagnostics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons mapping
const getCategoryIcon = (category) => {
  const icons = {
    truck_repair: Wrench,
    pressure_washing: Droplets,
    load_board: Truck,
    grocery_store: ShoppingCart,
    wholesale: Globe
  };
  return icons[category] || Building2;
};

const getCategoryColor = (category) => {
  const colors = {
    truck_repair: { bg: 'bg-blue-100', text: 'text-blue-600', darkBg: 'bg-blue-900/30', darkText: 'text-blue-400' },
    pressure_washing: { bg: 'bg-sky-100', text: 'text-sky-600', darkBg: 'bg-sky-900/30', darkText: 'text-sky-400' },
    load_board: { bg: 'bg-amber-100', text: 'text-amber-600', darkBg: 'bg-amber-900/30', darkText: 'text-amber-400' },
    grocery_store: { bg: 'bg-emerald-100', text: 'text-emerald-600', darkBg: 'bg-emerald-900/30', darkText: 'text-emerald-400' },
    wholesale: { bg: 'bg-slate-100', text: 'text-slate-600', darkBg: 'bg-slate-700', darkText: 'text-slate-300' }
  };
  return colors[category] || { bg: 'bg-purple-100', text: 'text-purple-600', darkBg: 'bg-purple-900/30', darkText: 'text-purple-400' };
};

const getCategoryLabel = (category) => {
  const labels = {
    truck_repair: 'Mechanic Shop',
    pressure_washing: 'Pressure Washing',
    load_board: 'Load Board',
    grocery_store: 'Grocery Store',
    wholesale: 'Wholesale'
  };
  return labels[category] || category;
};

export default function AdminDashboard() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, businessesRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/businesses`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setBusinesses(businessesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  };

  // Group businesses by category
  const businessesByCategory = businesses.reduce((acc, business) => {
    const cat = business.category || 'truck_repair';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(business);
    return acc;
  }, {});

  // Count users per business
  const getUsersForBusiness = (businessId) => {
    return users.filter(u => u.business_id === businessId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
          Platform Overview
        </h1>
        <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Manage categories, companies, and users
        </p>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                <Layers className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {Object.keys(businessesByCategory).length}
                </p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Link to="/admin/businesses">
          <Card className={`${isDark ? 'bg-slate-800 border-slate-700 hover:border-blue-500' : 'bg-white border-slate-200 hover:border-blue-400'} transition-colors cursor-pointer`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                  <Building2 className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {stats?.total_businesses || 0}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/users">
          <Card className={`${isDark ? 'bg-slate-800 border-slate-700 hover:border-emerald-500' : 'bg-white border-slate-200 hover:border-emerald-400'} transition-colors cursor-pointer`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                  <Users className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {stats?.total_users || 0}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Clover diagnostics — super-admin only */}
      <section>
        <CloverDiagnostics isDark={isDark} />
      </section>


      {/* SECTION 1: Categories */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <Layers className="w-5 h-5" /> Categories
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['truck_repair', 'wholesale', 'grocery_store'].map((category) => {
            const Icon = getCategoryIcon(category);
            const colors = getCategoryColor(category);
            const count = businessesByCategory[category]?.length || 0;
            
            return (
              <Card key={category} className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center ${isDark ? colors.darkBg : colors.bg}`}>
                    <Icon className={`w-5 h-5 ${isDark ? colors.darkText : colors.text}`} />
                  </div>
                  <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {getCategoryLabel(category)}
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {count} {count === 1 ? 'company' : 'companies'}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* SECTION 2: Companies by Category */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <Building2 className="w-5 h-5" /> Companies
          </h2>
          <Link to="/admin/businesses">
            <Button variant="outline" size="sm" className={isDark ? 'border-slate-600' : 'border-slate-200'}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        
        <div className="space-y-4">
          {Object.entries(businessesByCategory).map(([category, categoryBusinesses]) => {
            const Icon = getCategoryIcon(category);
            const colors = getCategoryColor(category);
            
            return (
              <Card key={category} className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                <CardHeader className={`py-3 px-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${isDark ? colors.darkBg : colors.bg}`}>
                      <Icon className={`w-4 h-4 ${isDark ? colors.darkText : colors.text}`} />
                    </div>
                    <CardTitle className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      {getCategoryLabel(category)}
                    </CardTitle>
                    <Badge className={`ml-auto ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                      {categoryBusinesses.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-100'}`}>
                    {categoryBusinesses.slice(0, 3).map((business) => {
                      const businessUsers = getUsersForBusiness(business.id);
                      
                      return (
                        <Link 
                          key={business.id}
                          to={`/${business.slug}`}
                          className={`flex items-center justify-between p-3 ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {business.logo ? (
                              <img src={business.logo} alt="" className="w-8 h-8 rounded-lg object-cover" />
                            ) : (
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                              </div>
                            )}
                            <div>
                              <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                {business.name}
                              </p>
                              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                /{business.slug} • {businessUsers.length} users
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {business.is_active ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 text-xs">Inactive</Badge>
                            )}
                            <ExternalLink className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                          </div>
                        </Link>
                      );
                    })}
                    {categoryBusinesses.length > 3 && (
                      <Link 
                        to="/admin/businesses" 
                        className={`block p-2 text-center text-xs font-medium ${isDark ? 'text-purple-400 hover:bg-slate-700/50' : 'text-purple-600 hover:bg-slate-50'}`}
                      >
                        View all {categoryBusinesses.length} companies →
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {Object.keys(businessesByCategory).length === 0 && (
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="py-8 text-center">
                <Building2 className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No companies yet</p>
                <Link to="/admin/businesses">
                  <Button className="mt-3 bg-purple-600 hover:bg-purple-700 text-white" size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Add Company
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* SECTION 3: Recent Users */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <Users className="w-5 h-5" /> Users
          </h2>
          <Link to="/admin/users">
            <Button variant="outline" size="sm" className={isDark ? 'border-slate-600' : 'border-slate-200'}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="py-8 text-center">
                <Users className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No users yet</p>
              </div>
            ) : (
              <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-slate-100'}`}>
                {users.slice(0, 5).map((user) => (
                  <div key={user.id} className={`flex items-center justify-between p-3 ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-purple-100'}`}>
                        <span className={`font-semibold text-xs ${isDark ? 'text-slate-300' : 'text-purple-600'}`}>
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                          {user.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {user.business_name}
                      </span>
                      <Badge className={user.role === 'owner' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                        {user.role}
                      </Badge>
                    </div>
                  </div>
                ))}
                {users.length > 5 && (
                  <Link 
                    to="/admin/users" 
                    className={`block p-2 text-center text-xs font-medium ${isDark ? 'text-purple-400 hover:bg-slate-700/50' : 'text-purple-600 hover:bg-slate-50'}`}
                  >
                    View all {users.length} users →
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
